import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { ComingSoon } from "@/components/coming-soon";
import { useAppForm } from "@/components/form";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldTitle,
} from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { Switch } from "@/components/ui/switch";
import {
	isMidRollEnabled,
	isPostRollEnabled,
	isPreRollEnabled,
	MonetizationStatus,
	toggleMonetizationStatus,
} from "@/enums/monetization-status";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId/monetization")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: channel } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.channels.updateMonetization.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.channels.key() });
				toast.success("Channel monetization updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this channel monetization.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			MonetizationStatusID: channel.MonetizationStatusID,
			IntroAdJingleID: channel.IntroAdJingleID || null,
			ExtroAdJingleID: channel.ExtroAdJingleID || null,
			IntroJingleID: channel.IntroJingleID || null,
			ExtroJingleID: channel.ExtroJingleID || null,
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { error } = await safe(
					updateMutation.mutateAsync({
						...value,
						ID: channel.ID,
					}),
				);
				if (error) {
					return validationErrors(error) ?? "An error occurred while updating the channel.";
				}
			},
		},
	});

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Channel Monetization</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<form.AppField
								name="MonetizationStatusID"
								children={(field) => (
									<FieldGroup className="gap-2">
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Monetization</FieldTitle>
													<FieldDescription>
														Enable the monetization for this podcast
													</FieldDescription>
												</FieldContent>
												<Switch
													checked={
														(field.state.value ?? MonetizationStatus.Undefined) >
														MonetizationStatus.Undefined
													}
													onCheckedChange={(checked) =>
														field.handleChange(
															checked
																? MonetizationStatus.PrerollMidrollPostroll
																: MonetizationStatus.Deactivated,
														)
													}
												/>
											</Field>
										</FieldLabel>
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Pre-roll Ads</FieldTitle>
													<FieldDescription>
														Pre-roll ads are played before the episode starts.
													</FieldDescription>
												</FieldContent>
												<Switch
													checked={isPreRollEnabled(
														field.state.value ?? MonetizationStatus.Undefined,
													)}
													onCheckedChange={() =>
														field.handleChange(
															toggleMonetizationStatus(
																field.state.value ?? MonetizationStatus.Undefined,
																"preroll",
															),
														)
													}
												/>
											</Field>
										</FieldLabel>
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Mid-roll Ads</FieldTitle>
													<FieldDescription>
														Mid-roll ads are played by interrupting the episode playback.
													</FieldDescription>
												</FieldContent>
												<Switch
													checked={isMidRollEnabled(
														field.state.value ?? MonetizationStatus.Undefined,
													)}
													onCheckedChange={() =>
														field.handleChange(
															toggleMonetizationStatus(
																field.state.value ?? MonetizationStatus.Undefined,
																"midroll",
															),
														)
													}
												/>
											</Field>
										</FieldLabel>
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Post-roll Ads</FieldTitle>
													<FieldDescription>
														Post-roll ads are played after the episode is completed.
													</FieldDescription>
												</FieldContent>
												<Switch
													checked={isPostRollEnabled(
														field.state.value ?? MonetizationStatus.Undefined,
													)}
													onCheckedChange={() =>
														field.handleChange(
															toggleMonetizationStatus(
																field.state.value ?? MonetizationStatus.Undefined,
																"postroll",
															),
														)
													}
												/>
											</Field>
										</FieldLabel>
									</FieldGroup>
								)}
							/>
						</FormCardContent>
					</FormCard>
					<FormCard className="xl:col-span-6" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Ad break Jingles</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<ComingSoon />
						</FormCardContent>
					</FormCard>
					<FormCard className="xl:col-span-6" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Episode Jingles</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<ComingSoon />
						</FormCardContent>
					</FormCard>
					<form.FormSaveBar />
				</div>
				<form.FormBottomBar />
			</form>
		</form.AppForm>
	);
}
